import { Component, computed, effect, input, output, signal, untracked, viewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { form, FormField, min } from '@angular/forms/signals';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { Popover } from '@openng/optimus-ui/popover';
import { ButtonComponent } from '../../button/button.component';
import { BaseInputComponentV2 } from '../base-input.component-v2';
import { LocalDatePipe } from '../../../../modules/shared/pipes/local-date.pipe';
import { ShortDatePipe } from '../../../../modules/shared/pipes/short-date.pipe';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { NumberInputComponent } from '../text-input-v2/number-input.component';
import { DateScrollerPeriod, DateScrollerRange, DateScrollerSelection } from './date-scroller-input.types';

@Component({
    selector: 'app-date-scroller-input',
    templateUrl: './date-scroller-input.component.html',
    styleUrl: './date-scroller-input.component.scss',
    imports: [
        FormsModule,
        DatePicker,
        Popover,
        ButtonComponent,
        LocalDatePipe,
        ShortDatePipe,
        FormField,
        NumberInputComponent,
    ],
})
export class DateScrollerInputComponent extends BaseInputComponentV2<Date | null> {

    protected readonly datePicker = viewChild(DatePicker);
    protected readonly datePickerModel = viewChild(NgModel);
    public readonly period = input<DateScrollerPeriod>(TimePeriodEnum.Day);
    public readonly periodSelection = input<DateScrollerSelection | null>(null);
    public readonly periodSelectionChange = output<DateScrollerSelection>();
    public readonly enablePeriodChange = input(false);
    public readonly dateRangeChange = output<DateScrollerRange>();

    protected readonly periodFormData = signal({
        length: 1,
        type: TimePeriodEnum.Day as DateScrollerPeriod,
    });
    protected readonly periodForm = form(this.periodFormData, schema => min(schema.length, 1));
    protected readonly activePeriod = computed(() => (
        this.enablePeriodChange()
            ? this.periodSelection()?.type ?? this.periodFormData().type
            : this.period()
    ));
    protected readonly periodLength = computed(() => (
        this.enablePeriodChange()
            ? Math.max((this.periodSelection()?.length ?? this.periodFormData().length) || 1, 1)
            : 1
    ));

    protected readonly range = computed<DateScrollerRange>(() => {
        const value = this.value();
        const date = value ? new Date(value) : new Date();
        const dateFrom = new Date(date);
        const dateTo = new Date(date);
        const type = this.activePeriod();
        const length = this.periodLength();

        dateFrom.setHours(0, 0, 0, 0);

        if (type === TimePeriodEnum.Week) {
            const dayOffset = (dateFrom.getDay() + 6) % 7;
            dateFrom.setDate(dateFrom.getDate() - dayOffset);
            dateTo.setTime(dateFrom.getTime());
            dateTo.setDate(dateTo.getDate() + length * 7 - 1);
        } else if (type === TimePeriodEnum.Month) {
            dateFrom.setDate(1);
            dateTo.setTime(dateFrom.getTime());
            dateTo.setMonth(dateTo.getMonth() + length, 0);
        } else {
            dateTo.setTime(dateFrom.getTime());
            dateTo.setDate(dateTo.getDate() + length - 1);
        }
        dateTo.setHours(23, 59, 59, 999);

        return { dateFrom, dateTo, type, length };
    });
    protected readonly pickerSelection = computed(() => {
        const { dateFrom, dateTo } = this.range();
        return [dateFrom, dateTo];
    });
    protected readonly singleDay = computed(() => (
        this.range().dateFrom.toDateString() === this.range().dateTo.toDateString()
    ));
    protected readonly singleMonth = computed(() => (
        this.activePeriod() === TimePeriodEnum.Month && this.periodLength() === 1
    ));

    protected readonly pickerView = computed<'date' | 'month'>(() => (
        this.activePeriod() === TimePeriodEnum.Month ? 'month' : 'date'
    ));
    protected readonly pickerMonthCount = computed(() => {
        if (this.pickerView() === 'month') return 1;
        const { dateFrom, dateTo } = this.range();
        const spansMonths = dateFrom.getFullYear() !== dateTo.getFullYear()
            || dateFrom.getMonth() !== dateTo.getMonth();
        return spansMonths ? 2 : 1;
    });
    protected readonly pickerResponsiveOptions = [{ breakpoint: '640px', numMonths: 1 }];

    constructor() {
        super();

        effect(() => {
            const type = this.period();
            const selection = this.periodSelection();
            untracked(() => this.periodFormData.update(value => {
                const nextType = selection?.type ?? type;
                const nextLength = selection?.length ?? value.length;
                return value.type === nextType && value.length === nextLength
                    ? value
                    : { type: nextType, length: nextLength };
            }));
        });

        effect(() => this.dateRangeChange.emit(this.range()));

        effect(onCleanup => {
            const model = this.datePickerModel();
            if (!model) return;

            // NgModel updates the DatePicker after rendering and resets its visible month.
            const subscription = model.control.valueChanges.subscribe(() => this.syncPickerMonths());
            onCleanup(() => subscription.unsubscribe());
        });
    }

    protected syncPickerMonths(): void {
        const picker = this.datePicker();
        if (!picker) return;

        const dateFrom = this.range().dateFrom;
        const month = dateFrom.getMonth();
        const year = dateFrom.getFullYear();
        if (picker.currentMonth === month && picker.currentYear === year
            && picker.months?.length === this.pickerMonthCount()) return;

        picker.currentMonth = month;
        picker.currentYear = year;
        picker.createMonths(month, year);
        picker.cd.detectChanges();
    }

    protected move(direction: -1 | 1): void {
        const date = new Date(this.value() ?? new Date());
        const period = this.activePeriod();
        const length = this.periodLength();
        if (period === TimePeriodEnum.Month) {
            const day = date.getDate();
            date.setDate(1);
            date.setMonth(date.getMonth() + direction * length);
            const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
            date.setDate(Math.min(day, lastDay));
        } else {
            const periodDays = period === TimePeriodEnum.Week ? 7 : 1;
            date.setDate(date.getDate() + direction * length * periodDays);
        }
        date.setHours(0, 0, 0, 0);
        this.value.set(date);
        this.touch.emit();
    }

    protected select(date: Date | null, popover: Popover): void {
        if (!date) return;
        const selected = new Date(date);
        selected.setHours(0, 0, 0, 0);
        this.value.set(selected);
        this.touch.emit();
        popover.hide();
    }

    protected setPeriod(type: DateScrollerPeriod): void {
        const selection = this.periodSelection();
        if (selection) {
            if (selection.type !== type) this.periodSelectionChange.emit({ ...selection, type });
            return;
        }
        this.periodFormData.update(value => ({ ...value, type }));
    }

    protected onPeriodLengthChange(length: number | null): void {
        const selection = this.periodSelection();
        if (!selection) return;

        const nextLength = Math.max(Number(length) || 1, 1);
        if (selection.length !== nextLength) this.periodSelectionChange.emit({ ...selection, length: nextLength });
    }

    protected readonly TimePeriodEnum = TimePeriodEnum;

}
